import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { REACT_APP_SERVER_LINK } from "../utils";
import { clearStoredSession, readStoredToken, SESSION_EXPIRED_EVENT } from "../utils/session";

const apiClient = axios.create({
  baseURL: REACT_APP_SERVER_LINK,
  timeout: 30000,
});

apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = readStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: token,
    } as AxiosRequestConfig["headers"];
  }
  return config;
});

const errorText = (error: AxiosError): string => {
  const data = error.response?.data as { message?: string; error?: string } | string | undefined;
  if (typeof data === "string") {
    return data;
  }
  return data?.message || data?.error || error.message || "";
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const sentToken = Boolean(
      (error.config?.headers as Record<string, unknown> | undefined)?.Authorization
    );
    const isAuthRequest = /\/api\/auth\//.test(error.config?.url || "");

    if (status === 403 && /suspend/i.test(errorText(error))) {
      clearStoredSession();
      window.location.assign("/account/suspended");
      return Promise.reject(error);
    }

    // A signed-in request was rejected: the session expired or was revoked.
    if (status === 401 && sentToken && !isAuthRequest) {
      clearStoredSession();
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      if (!window.location.pathname.startsWith("/credentials")) {
        const next = `${window.location.pathname}${window.location.search}`;
        window.location.assign(`/credentials?reason=expired&next=${encodeURIComponent(next)}`);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
